import { useEffect, useState } from 'react';

import { type QueryKey, useQuery } from '@tanstack/react-query';

import type { LivestreamResponse } from '@cobliteam/common-types';

import { LivestreamRequester } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';

import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const livestreamRequester = new LivestreamRequester({
  host: env.apiHost,
});
const QUERY_KEY: QueryKey = ['CAMERA-LIVESTREAM'];

const TIME_FROM_LAST_MESSAGE_IN_SECONDS = 90;
const TIME_FROM_LAST_MESSAGE_IN_MILLIS = TIME_FROM_LAST_MESSAGE_IN_SECONDS * 1000;

const useDeviceLivestream = () => {
  const getDeviceLivestream = (deviceId: string) => {
    return useQuery<LivestreamResponse>({
      queryKey: [...QUERY_KEY, deviceId],
      queryFn: () => livestreamRequester.getLivestream({ deviceId }),
    });
  };

  return {
    getDeviceLivestream,
  };
};

interface UseLivestreamProps {
  device: DeviceFragment | undefined;
}

const useLivestreamAvailable = ({ device }: UseLivestreamProps) => {
  const [result, setResult] = useState<boolean>(false);

  const isWithinTimeFrame = (dateInMillis?: number | null) => {
    if (!dateInMillis) return false;
    return Date.now() - dateInMillis <= TIME_FROM_LAST_MESSAGE_IN_MILLIS;
  };

  useEffect(() => {
    const livestreamAvailable = isWithinTimeFrame(device?.lastLocation?.timeInMillis);

    setResult(livestreamAvailable);
  }, [device]);

  return result;
};

export default useDeviceLivestream;

export { useLivestreamAvailable };
